import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from '../components/CustomIcons';
import LogoImg from '../components/LogoImg';

const DEMO_ACCOUNTS = [
  { label: 'Learner (Jonathan)', email: 'learner1@demo.com', password: 'demo123', role: 'learner' },
  { label: 'Admin (Dr. Mitchell)', email: 'admin@demo.com', password: 'demo123', role: 'facility_admin' },
  { label: 'Employer (Mike Torres)', email: 'employer@demo.com', password: 'demo123', role: 'employer_partner' },
  { label: 'Provider (Dallas College)', email: 'provider@demo.com', password: 'demo123', role: 'provider_partner' },
  { label: 'Case Manager (Lisa)', email: 'casemanager@demo.com', password: 'demo123', role: 'case_manager' },
  { label: 'Instructor (Robert)', email: 'instructor@demo.com', password: 'demo123', role: 'instructor' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'facility_admin') navigate('/admin');
      else if (user.role === 'case_manager') navigate('/case-manager');
      else if (user.role === 'instructor') navigate('/instructor');
      else if (user.role === 'employer_partner') navigate('/jobs/employer');
      else if (user.role === 'provider_partner') navigate('/marketplace/provider');
      else navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (account) => {
    setEmail(account.email);
    setPassword(account.password);
    setError('');
  };

  const inputStyle = {
    background: '#0D0D0D',
    border: '1px solid #333333',
    borderRadius: '10px',
    color: '#FFFFFF',
    padding: '12px 16px',
    width: '100%',
    fontFamily: 'Inter, sans-serif',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    minHeight: '44px',
  };

  return (
    /* Solid dark page — no carousel, no pattern overlay */
    <div style={{
      minHeight: '100vh',
      background: '#0D0D0D',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div className="animate-fade-in" style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <div style={{ background: '#111111', border: '1px solid #222222', borderRadius: '10px', padding: '10px 20px', display: 'inline-block', marginBottom: '8px' }}>
              <LogoImg forceDark height={48} alt="Assistiv OS" style={{ display: 'block' }} />
            </div>
          </Link>
          <p style={{ fontSize: '12px', color: '#8C8C8C', fontFamily: 'Inter, sans-serif', marginBottom: '4px' }}>
            Correctional Education &amp; Reentry OS
          </p>
          <p style={{ fontSize: '11px', color: '#666666', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
            Earn credentials. Plan your reentry. Walk out ready.
          </p>
        </div>

        {/* Form card — solid background, no bleed from behind */}
        <div style={{
          background: '#1A1A1A',
          border: '1px solid #333333',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
        }}>
          <h1 style={{
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 700,
            fontSize: '22px',
            color: '#FFFFFF',
            marginBottom: '6px',
            marginTop: 0,
          }}>
            Sign in to your account
          </h1>
          <p style={{ color: '#A3A3A3', fontSize: '14px', fontFamily: 'Inter, sans-serif', marginBottom: '24px' }}>
            Enter your credentials to continue learning
          </p>

          {error && (
            <div
              role="alert"
              aria-live="polite"
              data-testid="login-error"
              style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '16px',
                color: '#EF4444',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="login-email"
                style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#A3A3A3', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}
              >
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                aria-required="true"
                data-testid="login-email"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#F97316'}
                onBlur={e => e.target.style.borderColor = '#333333'}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label
                htmlFor="login-password"
                style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#A3A3A3', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}
              >
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  aria-required="true"
                  data-testid="login-password"
                  style={{ ...inputStyle, paddingRight: '44px' }}
                  onFocus={e => e.target.style.borderColor = '#F97316'}
                  onBlur={e => e.target.style.borderColor = '#333333'}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#8C8C8C',
                    padding: '4px', minHeight: '44px', display: 'flex', alignItems: 'center',
                  }}
                >
                  {showPass ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              data-testid="login-submit"
              aria-busy={loading}
              style={{ width: '100%', justifyContent: 'center', fontSize: '15px', padding: '13px', opacity: loading ? 0.7 : 1, minHeight: '44px' }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '18px' }}>
            <Link to="/signup" style={{ color: '#F97316', fontSize: '14px', fontFamily: 'Inter, sans-serif', textDecoration: 'none' }}>
              Don&apos;t have an account?{' '}
              <span style={{ fontWeight: 600 }}>Sign Up</span>
            </Link>
          </div>
        </div>

        {/* Demo accounts */}
        <div style={{
          marginTop: '20px',
          background: '#111111',
          border: '1px solid #222222',
          borderRadius: '12px',
          padding: '16px',
        }}>
          <p style={{ fontSize: '12px', color: '#8C8C8C', fontFamily: 'Inter, sans-serif', marginBottom: '10px', textAlign: 'center' }}>
            Try a demo account:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {DEMO_ACCOUNTS.map(acc => (
              <button
                key={acc.email}
                onClick={() => fillDemo(acc)}
                data-testid={`demo-${acc.role}`}
                style={{
                  background: '#1A1A1A', border: '1px solid #333333', borderRadius: '8px',
                  padding: '6px 10px', fontSize: '11px', fontFamily: 'Inter, sans-serif',
                  color: '#A3A3A3', cursor: 'pointer', transition: 'all 0.15s', minHeight: '36px',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#F97316'; e.currentTarget.style.color = '#F97316'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#333333'; e.currentTarget.style.color = '#A3A3A3'; }}
              >
                {acc.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
